import React, { useEffect, useContext } from "react";
import { useHistory } from "react-router";
import textile from "textile-js";
import { useTranslation } from "react-i18next";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import Container from "@material-ui/core/Container";
import { Auth } from "aws-amplify";

import AppContext from "../contexts/AppContext";
import { setPageTitle } from "../actions";
import componentStyles from "../assets/styles/containers/issue_detail";
import Header from "../components/header";
import IssueForm from "../components/issue_form";
import NoteForm from "../components/note_form";
import NoteList from "../components/note_list";
import { useIssueDetail } from "../hooks/useIssueDetail";
import CustomBox from "../components/templates/customBox";
import TextileView from "../components/atoms/textile_view";

const useStyles = makeStyles(componentStyles);

const IssueDetail = (props) => {
  const { loading, setLoading, readissueDetail, issueDetail, errors, init } =
    useIssueDetail();
  const { state, dispatch } = useContext(AppContext);
  const redmineurl = state.current.redmineurl;
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const conditions = {
    id: props.match.params.id,
  };

  useEffect(() => {
    setLoading(true);
    if (init) {
      Auth.currentUserInfo().then(async (res) => {
        if (res) {
          await readissueDetail(conditions);
          setPageTitle(dispatch, t("label_issue"));
        } else {
          history.push("/");
          Auth.signOut();
        }
      });
    }
  }, [init]);

  return (
    <>
      <Header />
      <Container
        maxWidth={false}
        component={Box}
        marginTop="-6rem"
        classes={{ root: classes.containerRoot }}
      >
        <Grid container>
          <Grid item component={Box}>
            <Box marginBottom="1.5rem!important">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={(e) => history.goBack()}
              >
                {t("issue_detail.button_back_to_list")}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <CustomBox loading={loading} errors={errors}>
          <Grid container justify="center">
            <Grid item xs={12} component={Box} marginBottom="3rem!important">
              <Card classes={{ root: classes.cardRoot }}>
                <CardHeader
                  classes={{ root: classes.cardHeaderRoot }}
                  subheader={
                    <Grid
                      container
                      component={Box}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs="auto">
                        <Box
                          component={Typography}
                          variant="h3"
                          marginBottom="0!important"
                        >
                          {`${issueDetail.subject} #${issueDetail.id}`}
                        </Box>
                      </Grid>
                      <Grid item xs="auto">
                        <Box
                          justifyContent="flex-end"
                          display="flex"
                          flexWrap="wrap"
                        >
                          <IssueForm mode="edit" />
                          <Button
                            href={`${redmineurl}issues/${issueDetail.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="small"
                          >
                            {t("issue_detail.button_open_redmine")}
                            <Box
                              component={OpenInNewIcon}
                              width="1rem!important"
                              height="1rem!important"
                            />
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  }
                ></CardHeader>
                <CardContent>
                  <Grid container component={Box}>
                    <Grid item xs={12}>
                      <Card
                        classes={{ root: classes.detailCardRoot }}
                        elevation={0}
                      >
                        <CardContent>
                          <Grid container>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_status")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.status
                                    ? issueDetail.status.name
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_author")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.author
                                    ? issueDetail.author.name
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_assigned_to")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.assigned_to
                                    ? issueDetail.assigned_to.name
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_start_date")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.start_date
                                    ? issueDetail.start_date
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_due_date")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.due_date
                                    ? issueDetail.due_date
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box
                                component={Typography}
                                variant="h5"
                                marginBottom="0.1rem!important"
                              >
                                <Box
                                  component="span"
                                  color={theme.palette.gray[600]}
                                >
                                  {t("field_tracker")}
                                </Box>
                              </Box>
                              <Box
                                component={Typography}
                                variant="h4"
                                fontWeight="600!important"
                                marginBottom="1.0rem!important"
                              >
                                <Box component="span">
                                  {issueDetail.tracker
                                    ? issueDetail.tracker.name
                                    : "-"}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card
                        classes={{ root: classes.detailCardRoot }}
                        elevation={0}
                      >
                        <CardContent>
                          <Box
                            component={Typography}
                            variant="h5"
                            marginBottom="0.1rem!important"
                          >
                            <Box
                              component="span"
                              color={theme.palette.gray[600]}
                            >
                              説明
                            </Box>
                          </Box>

                          <Box
                            className={classes.desc}
                            color={theme.palette.gray[900]}
                          >
                            <TextileView text={issueDetail.description} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} component={Box}>
              <Card classes={{ root: classes.cardRoot }}>
                <CardHeader
                  subheader={
                    <Grid
                      container
                      component={Box}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs="auto">
                        <Box
                          component={Typography}
                          variant="h3"
                          marginBottom="0!important"
                        >
                          {t("issue_detail.field_notes")}
                        </Box>
                      </Grid>
                      <Grid item xs="auto">
                        <Box
                          justifyContent="flex-end"
                          display="flex"
                          flexWrap="wrap"
                        >
                          <NoteForm id={issueDetail.id} />
                        </Box>
                      </Grid>
                    </Grid>
                  }
                  classes={{ root: classes.cardHeaderRoot }}
                ></CardHeader>
                <NoteList issueDetail={issueDetail} />
              </Card>
            </Grid>
          </Grid>
        </CustomBox>
      </Container>
    </>
  );
};

export default IssueDetail;
